import type { SelectorGroup } from '@/modules/lab/catalog/components/selectorTypes'
import { Autocomplete, TextField } from '@mui/material'
import { useMemo } from 'react'

export type InlineSelectorFieldProps = {
  label: string
  value: string
  groups: SelectorGroup[]
  onChange: (value: string) => void
  listLabel: string
  placeholder?: string
  searchPlaceholder?: string
  required?: boolean
  disabled?: boolean
  helperText?: string
  error?: string
  emptyLabel?: string
}

type GroupedOption = {
  value: string
  label: string
  description?: string
  groupLabel: string
}

export function InlineSelectorField({
  label,
  value,
  groups,
  onChange,
  placeholder = 'Select…',
  required,
  disabled,
  helperText,
  error,
}: InlineSelectorFieldProps) {
  const options = useMemo<GroupedOption[]>(
    () =>
      groups.flatMap((g) =>
        g.options.map((o) => ({
          value: o.value,
          label: o.label,
          description: o.description,
          groupLabel: g.label,
        })),
      ),
    [groups],
  )

  const selected = useMemo(
    () => options.find((o) => o.value === value) ?? null,
    [options, value],
  )

  return (
    <Autocomplete
      size="small"
      disabled={disabled}
      options={options}
      value={selected}
      groupBy={(opt) => opt.groupLabel}
      getOptionLabel={(opt) => opt.label}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      onChange={(_, opt) => onChange(opt?.value ?? '')}
      renderOption={(props, opt) => (
        <li {...props} key={opt.value}>
          <div>
            <div>{opt.label}</div>
            {opt.description ? (
              <div style={{ fontSize: '0.7rem', opacity: 0.7 }}>{opt.description}</div>
            ) : null}
          </div>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          placeholder={placeholder}
          error={Boolean(error)}
          helperText={error ?? helperText}
        />
      )}
    />
  )
}

/** @deprecated — use InlineSelectorField */
export const CatalogSelectorField = InlineSelectorField
export type CatalogSelectorFieldProps = InlineSelectorFieldProps
